import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Badge, Button, Alert, Spinner, 
  Form, Row, Col, Pagination, Dropdown 
} from 'react-bootstrap';
import NotificationService from '../../services/NotificationService';

const NotificationHistory = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [currentPage, filterType]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await NotificationService.getNotificationHistory(itemsPerPage, offset, filterType);
      
      setNotifications(response.notifications || []);
      setIsAdmin(response.isAdmin || false);
    } catch (err) {
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await NotificationService.getNotificationStats();
      setStats(response.stats || null);
    } catch (err) {
      // Stats only available for admin, ignore error for regular users
      console.log('Stats not available:', err.message);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await NotificationService.markAsRead(id);
      setSuccess('Notification marked as read');
      fetchNotifications();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setSuccess('All notifications marked as read');
      fetchNotifications();
      fetchStats();
    } catch (err) {
      setError(err.message || 'Failed to mark all as read');
    }
  };

  const getNotificationTypeBadge = (type) => {
    const typeColors = {
      'stream_started': 'success',
      'stream_stopped': 'warning',
      'stream_error': 'danger',
      'subscription_expiring': 'info',
      'subscription_expired': 'danger',
      'payment_success': 'success',
      'payment_failed': 'danger',
      'ticket_created': 'primary',
      'ticket_updated': 'info',
      'admin_alert': 'dark',
      'system': 'secondary'
    };
    
    // Handle admin_ prefixed types
    const cleanType = type?.replace('admin_', '') || 'unknown';
    return typeColors[cleanType] || 'secondary';
  };

  const getNotificationTypeLabel = (type) => {
    if (!type) return 'Unknown';
    return type.replace('admin_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredNotifications = notifications.filter(n => {
    if (filterRead === 'read') return n.is_read === 1;
    if (filterRead === 'unread') return n.is_read === 0;
    return true;
  });

  const totalPages = Math.ceil((stats?.total || notifications.length) / itemsPerPage);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h2 className="mb-4">
        <i className="fas fa-bell me-2"></i>
        Notification History
      </h2>
      
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      {/* Stats Cards - Only for Admin */}
      {isAdmin && stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="mb-0">{stats.total || 0}</h3>
                <small className="text-muted">Total Notifications</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="mb-0 text-warning">{stats.unread || 0}</h3>
                <small className="text-muted">Unread</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="mb-0 text-info">{stats.unique_users || 0}</h3>
                <small className="text-muted">Active Users</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="mb-0 text-primary">{stats.admin_notifications || 0}</h3>
                <small className="text-muted">Admin Alerts</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Type</Form.Label>
                <Form.Select 
                  value={filterType} 
                  onChange={(e) => { setFilterType(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Types</option>
                  <option value="stream_started">Stream Started</option>
                  <option value="stream_stopped">Stream Stopped</option>
                  <option value="stream_error">Stream Error</option>
                  <option value="subscription_expiring">Subscription Expiring</option>
                  <option value="subscription_expired">Subscription Expired</option>
                  <option value="payment_success">Payment Success</option>
                  <option value="payment_failed">Payment Failed</option>
                  <option value="ticket_created">Ticket Created</option>
                  <option value="ticket_updated">Ticket Updated</option>
                  <option value="admin_alert">Admin Alert</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select 
                  value={filterRead} 
                  onChange={(e) => setFilterRead(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="read">Read</option>
                  <option value="unread">Unread</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button 
                variant="outline-primary" 
                onClick={handleMarkAllAsRead}
                className="w-100"
              >
                <i className="fas fa-check-double me-2"></i>
                Mark All as Read
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Notifications Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            {isAdmin ? 'All System Notifications' : 'Your Notifications'}
          </h5>
        </Card.Header>
        <Card.Body>
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-bell-slash fa-3x text-muted mb-3"></i>
              <p className="text-muted">No notifications found</p>
            </div>
          ) : (
            <>
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Message</th>
                    {isAdmin && <th>User</th>}
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotifications.map((notification) => (
                    <tr key={notification.id} className={notification.is_read ? '' : 'table-warning'}>
                      <td>
                        {notification.is_read ? (
                          <Badge bg="secondary">Read</Badge>
                        ) : (
                          <Badge bg="warning">Unread</Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg={getNotificationTypeBadge(notification.type)}>
                          {getNotificationTypeLabel(notification.type)}
                        </Badge>
                      </td>
                      <td>
                        <strong>{notification.title}</strong>
                      </td>
                      <td>
                        <small>{notification.message}</small>
                      </td>
                      {isAdmin && (
                        <td>
                          {notification.username ? (
                            <span>{notification.username}</span>
                          ) : (
                            <Badge bg="dark">System</Badge>
                          )}
                        </td>
                      )}
                      <td>
                        <small className="text-muted">
                          {new Date(notification.created_at).toLocaleString()}
                        </small>
                      </td>
                      <td>
                        {!notification.is_read && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleMarkAsRead(notification.id)}
                          >
                            <i className="fas fa-check"></i>
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1} 
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                      disabled={currentPage === 1} 
                    />
                    
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                      disabled={currentPage === totalPages} 
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)} 
                      disabled={currentPage === totalPages} 
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NotificationHistory;
